/**
 * Verify-email landing page.
 *
 * The verification email contains a link of the form
 * ``https://aria-harmoniq.dev/auth/verify-email?token=...`` — that
 * URL routes to this component (public, no auth required). On
 * mount, we POST the token to the BFF's
 * ``POST /auth/email/verify`` proxy. The BFF talks to auth, which
 * flips ``email_verified=TRUE`` and ``master_enabled=TRUE`` in one
 * transaction, then returns ``{status: "success" | "invalid"}``
 * back through the BFF.
 *
 * Once we have the result, we navigate the browser to
 * ``/settings/notifications?verify=success`` (or
 * ``?verify=invalid``) where the Notifications page reads the
 * query param and renders a banner. The token is consumed exactly
 * once per click — clicking the link a second time produces a
 * silent no-op on the auth side because email_verified is already
 * TRUE.
 *
 * The user might or might not be logged in when they click the
 * link. The verify call itself doesn't need a session — the JWT in
 * the URL is the credential. Once the redirect lands them at
 * ``/settings/notifications`` the PrivateRoute guard will route an
 * unauthenticated user to /auth/sign-in with the settings page as
 * the post-login target.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import notificationService from "../../services/notificationService";

import "./VerifyEmail.css";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying");
  // Guard against React StrictMode's double-mount calling our
  // verify endpoint twice. The second call is a silent no-op on
  // the auth side, but we'd rather not burn the round-trip.
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const token = searchParams.get("token");
    if (!token) {
      navigate("/settings/notifications?verify=invalid", { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await notificationService.verify(token);
        const result = res?.data?.status ?? res?.status;
        const dest =
          result === "success"
            ? "/settings/notifications?verify=success"
            : "/settings/notifications?verify=invalid";
        navigate(dest, { replace: true });
      } catch {
        // Network error or upstream 502 — show a "try again" CTA
        // here rather than navigating, so the user knows to retry
        // rather than thinking the link itself is bad. We don't
        // gate on a ``cancelled`` flag because the calledRef
        // already prevents the StrictMode double-mount from
        // double-firing — and we WANT the error state to render
        // even after the cleanup phase (the user's still on this
        // page; nothing else owns the route).
        setStatus("error");
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div className="verify-email-shell">
      <div className="verify-email-card">
        <div className="verify-email-mark" aria-hidden="true">
          Harmon<span className="verify-email-mark-accent">IQ</span>
        </div>
        {status === "verifying" && (
          <>
            <div className="verify-email-spinner" aria-hidden="true" />
            <h1 className="verify-email-title">Verifying your email…</h1>
            <p className="verify-email-sub">
              This usually takes a second. Hold tight.
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="verify-email-mark-error" aria-hidden="true">
              !
            </div>
            <h1 className="verify-email-title">Could not reach our servers</h1>
            <p className="verify-email-sub">
              Something on our side timed out. Try the link again, or open
              Settings → Notifications to request a fresh verification email.
            </p>
            <button
              type="button"
              className="verify-email-btn"
              onClick={() => navigate("/settings/notifications", { replace: true })}
            >
              Open Settings
            </button>
          </>
        )}
      </div>
    </div>
  );
}
